import React, { useCallback, useMemo } from 'react'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../i18n/localeUtils'
import { Link } from 'react-router'
import { Box, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import {
    DataGridPro,
    GridCallbackDetails,
    GridColDef,
    GridColumnOrderChangeParams,
    GridFilterModel,
    GridSlots,
    gridClasses,
} from '@mui/x-data-grid-pro'
import { getMuiDataGridLocaleText } from '../../i18n/localeUtils'

import { companyGridCurrentPageAtom, companySearchTextfieldAtom } from '../../atoms/companyAtom'
import { formatBigMoney, formatDateYYYYMMDD } from '../../packages/utils/numberFormatters'
import { ItalicWrapper } from '../../utils/typography/ItalicWrapper'
import { CompanyName } from './CompanyName'
import { IssueTypeText } from './IssueType'
import { CompanyEntity } from '../../types/CompanyEntity'
import { CustomDataGridToolbar, CustomDataGridToolbarProps } from '../custom-grid-components/CustomDataGridToolbar'
import { FullHeightBox } from '../UIComponents'
import { translateMillistreamSector } from '../../utils/millistreamSectors'
import { CustomColumnsPanel, CustomColumnsPanelProps } from '../custom-grid-components/CustomColumnsPanel'
import { CompanyGridCols } from '@finance-lab/utils/types'

export const flagPath = (countryCode: string) => `/static/img/flags/${countryCode?.toLowerCase()}.png`

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga bolag hittades</Typography>
        </Box>
    )
}

const latestIssueAdvisorNames = (
    company: CompanyEntity,
    key: 'financialAdvisors' | 'legalAdvisors' | 'issuingAgents',
) =>
    company.latestIssue?.[key]
        ?.map((a) => a.name)
        .filter(Boolean)
        .join(', ') ?? ''

function CompaniesGrid({
    companies,
    detailsPath,
    availableColumns,
    columnOrder,
    visibleColumns,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onResetColumns,
    isCompact = false,
}: {
    companies: CompanyEntity[]
    detailsPath?: (company: { id: string }) => string
    availableColumns: CompanyGridCols[]
    columnOrder: CompanyGridCols[]
    visibleColumns: Partial<Record<CompanyGridCols, boolean>>
    onColumnOrderChange?: (settings: Array<CompanyGridCols>) => void
    onColumnVisibilityChange?: (settings: Partial<Record<CompanyGridCols, boolean>>) => void
    onResetColumns?: () => void
    isCompact?: boolean
}) {
    const [pageSize, setPageSize] = React.useState(10)

    const { t } = useTranslation()
    const { language: locale } = useAppLocale()
    const theme = useTheme()
    const isTabletScreen = useMediaQuery(theme.breakpoints.up('sm'))
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'))

    detailsPath = detailsPath || ((company: { id: string }) => `/companies/${company.id}`)

    const [companyGridCurrentPage, setCompanyGridCurrentPage] = useAtom(companyGridCurrentPageAtom)
    const [searchTextfield, setSearchTextfield] = useAtom(companySearchTextfieldAtom)

    // Initialize filterModel with quickFilterValues and an empty items array
    const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
        items: [], // Ensure items is an empty array initially
        quickFilterValues: [searchTextfield], // Default quick filter value
    })

    const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
        setFilterModel({
            ...newFilterModel,
            items: newFilterModel.items || [], // Ensure items is always an array
        })
        setCompanyGridCurrentPage(0)

        if (newFilterModel.quickFilterValues) {
            setSearchTextfield(newFilterModel.quickFilterValues[0])
        }
    }

    const sortString = (v1: string | null, v2: string | null) => {
        return (v1 ?? '').localeCompare(v2 ?? '')
    }

    const effectiveVisibleColumns = useMemo(() => {
        if (!isCompact) {
            return visibleColumns
        }

        const compact: Partial<Record<CompanyGridCols, boolean>> = {
            name: true,
            sector: true,
        }
        if (isTabletScreen) {
            compact.capSize = true
        }
        return compact
    }, [isCompact, isTabletScreen, visibleColumns])

    const allCols: Array<GridColDef<CompanyEntity> & { field: CompanyGridCols }> = useMemo(
        () => [
            {
                field: 'country',
                headerName: t('entities.tableHeaders.country'),
                width: 50,
                valueGetter: (_, row) => row.countryCode,
                sortComparator: sortString,
                display: 'flex',
                renderCell: (params) => (
                    <>
                        {params.value && (
                            <Tooltip title={params.value}>
                                <img src={flagPath(params.value)} width={25} alt='' />
                            </Tooltip>
                        )}
                    </>
                ),
            },
            {
                field: 'name',
                headerName: t('entities.tableHeaders.name'),
                flex: 0.8,
                display: 'flex',
                renderCell: (params) => {
                    if (isLargeScreen) {
                        return (
                            <Link to={detailsPath(params.row)}>
                                <CompanyName company={params.row} />
                            </Link>
                        )
                    }
                    return (
                        <Stack
                            direction='row'
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                                paddingY: { xs: 3, md: 4 },
                            }}
                        >
                            {params.row.countryCode ? (
                                <Box sx={{ height: '16px' }}>
                                    <img src={flagPath(params.row.countryCode)} width={25} alt='' />
                                </Box>
                            ) : null}
                            <Link to={detailsPath(params.row)}>
                                <CompanyName company={params.row} />
                            </Link>
                        </Stack>
                    )
                },
            },
            {
                field: 'sector',
                headerName: t('entities.tableHeaders.sector'),
                flex: 0.7,
                valueGetter: (_, row) => (row.isDelisted ? '-' : translateMillistreamSector(1, row.sector1)),
                renderCell: (params) => <FullHeightBox>{params.value}</FullHeightBox>,
            },
            {
                field: 'marketList',
                headerName: t('entities.tableHeaders.marketList'),
                flex: 0.7,
                valueGetter: (_, row) => (row.marketplaceName ? row.marketplaceName : row.delistedDate),
                sortComparator: sortString,
                display: 'flex',
                renderCell: (params) => {
                    const upcomingIpoDate =
                        params.row.shares.every((s) => s.upcomingIpoDate) &&
                        params.row.shares.find((s) => s.upcomingIpoDate)?.upcomingIpoDate
                    const hasCancelledIpo = params.row.shares.every((s) => !s.listedDate) && !upcomingIpoDate

                    return params.row.isDelisted ? (
                        <ItalicWrapper isItalic>
                            {t('pages.companies.delisted')} ({formatDateYYYYMMDD(params.row.delistedDate, { locale })})
                        </ItalicWrapper>
                    ) : upcomingIpoDate ? (
                        <ItalicWrapper isItalic>
                            {t('pages.companies.upcomingIpo', {
                                date: formatDateYYYYMMDD(upcomingIpoDate, { locale }),
                            })}
                        </ItalicWrapper>
                    ) : hasCancelledIpo ? (
                        <ItalicWrapper isItalic>{t('pages.companies.cancelledIpo')}</ItalicWrapper>
                    ) : (
                        (params.value as string | null)
                    )
                },
            },
            {
                field: 'capSize',
                headerName: t('entities.tableHeaders.marketCap'),
                flex: 0.5,
                valueGetter: (_, company) => (company.delistedDate ? 0 : company.marketcap),
                getSortComparator(sortDirection) {
                    return (v1: number | null, v2: number | null) => {
                        if (v1 === v2) {
                            return 0
                        }

                        if (v1 === 0 || v1 === null) {
                            return 1
                        } else if (v2 === 0 || v2 === null) {
                            return -1
                        }

                        const value1 = v1 ?? 0
                        const value2 = v2 ?? 0

                        if (sortDirection === 'asc') {
                            return value1 - value2
                        } else {
                            return value2 - value1
                        }
                    }
                },
                renderCell: (params) => {
                    if (params.row.delistedDate) return <FullHeightBox>-</FullHeightBox>
                    else if (params.value != 0) {
                        return (
                            <FullHeightBox>
                                {formatBigMoney(params.value, {
                                    locale,
                                    currencySymbol: params.row.currency,
                                })}
                            </FullHeightBox>
                        )
                    } else {
                        return null
                    }
                },
            },
            {
                field: 'latestIssue',
                headerName: t('entities.tableHeaders.latestIssue'),
                flex: 1,
                description: t('entities.tableHeaders.latestIssueDescription'),
                valueGetter: (_, row) => {
                    return row.latestIssue?.subStartDate || row.latestIssue?.publicationDate || null
                },
                sortComparator: (v1, v2) => {
                    const date1 = new Date(v1)
                    const date2 = new Date(v2)
                    return date1.getTime() - date2.getTime()
                },
                renderCell: (params) => {
                    if (params.row.latestIssue) {
                        return (
                            <Link to={`/issues/${params.row.latestIssue.id}`}>
                                <IssueTypeText issueType={params.row.latestIssue?.type} />{' '}
                                {formatDateYYYYMMDD(params.value, { locale })}
                            </Link>
                        )
                    } else {
                        return null
                    }
                },
            },
            {
                field: 'financialAdvisors',
                headerName: t('entities.tableHeaders.financialAdvisors'),
                flex: 0.8,
                sortable: false,
                valueGetter: (_, row) => latestIssueAdvisorNames(row, 'financialAdvisors'),
                renderCell: (params) => <FullHeightBox>{params.value}</FullHeightBox>,
            },
            {
                field: 'legalAdvisors',
                headerName: t('entities.tableHeaders.legalAdvisors'),
                flex: 0.8,
                sortable: false,
                valueGetter: (_, row) => latestIssueAdvisorNames(row, 'legalAdvisors'),
                renderCell: (params) => <FullHeightBox>{params.value}</FullHeightBox>,
            },
            {
                field: 'issuingAgents',
                headerName: t('entities.tableHeaders.issuingAgents'),
                flex: 0.8,
                sortable: false,
                valueGetter: (_, row) => latestIssueAdvisorNames(row, 'issuingAgents'),
                renderCell: (params) => <FullHeightBox>{params.value}</FullHeightBox>,
            },
        ],
        [detailsPath, isLargeScreen, locale, t],
    )

    const columns = useMemo(
        () =>
            allCols
                .filter((col) => availableColumns.includes(col.field))
                .sort((a, b) => columnOrder.indexOf(a.field) - columnOrder.indexOf(b.field)),
        [allCols, availableColumns, columnOrder],
    )

    const columnVisibilityModel = useMemo(
        () =>
            availableColumns.reduce(
                (acc, col) => ({
                    ...acc,
                    [col]: effectiveVisibleColumns[col] !== false,
                }),
                {} as Record<CompanyGridCols, boolean>,
            ),
        [availableColumns, effectiveVisibleColumns],
    )

    const handleColumnOrderChange = (
        _params: GridColumnOrderChangeParams,
        _event: unknown,
        details: GridCallbackDetails,
    ) => {
        onColumnOrderChange?.(details.api.getAllColumns().map((c) => c.field) as CompanyGridCols[])
    }

    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    const hasColumnCustomization = !isCompact && !!onColumnVisibilityChange

    const ToolbarSlot = useCallback(
        (props: CustomDataGridToolbarProps) => (
            <CustomDataGridToolbar {...props} showColumnCustomization={hasColumnCustomization} />
        ),
        [hasColumnCustomization],
    )

    return (
        <DataGridPro
            sx={{
                ...disableSelectedSellOutlineStyle,
                '& .MuiDataGrid-main': { width: 0, minWidth: '100%' },
                width: '100%',
                '& .even': {
                    backgroundColor: theme.palette.rowColor.main,
                },
                '& .MuiDataGrid-columnHeader': hasColumnCustomization ? { cursor: 'move' } : undefined,
            }}
            localeText={getMuiDataGridLocaleText(locale)}
            disableColumnMenu
            disableRowSelectionOnClick
            rows={companies}
            columns={columns}
            density='standard'
            getRowHeight={() => (!isLargeScreen ? 'auto' : null)}
            pagination={true}
            pageSizeOptions={[10, 25, 50, 100]}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
            slots={{
                noResultsOverlay: CustomNoResultsOverlay,
                toolbar: ToolbarSlot,
                columnsPanel: hasColumnCustomization ? (CustomColumnsPanel as GridSlots['columnsPanel']) : undefined,
            }}
            slotProps={{
                columnsPanel: {
                    onColumnVisibilityChange,
                    onResetColumns,
                } as CustomColumnsPanelProps,
            }}
            paginationModel={{ pageSize, page: companyGridCurrentPage }}
            onPaginationModelChange={({ page, pageSize: newPageSize }) => {
                setCompanyGridCurrentPage(page)
                setPageSize(newPageSize)
            }}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(model) => onColumnVisibilityChange?.(model)}
            onColumnOrderChange={hasColumnCustomization ? handleColumnOrderChange : undefined}
            showToolbar
        />
    )
}

export default CompaniesGrid
